import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { toast } from 'sonner';
import { Crosshair, Expand, Minimize2, Minus, Plus, Search, Maximize, X } from 'lucide-react';

import { aUnePosition, cleVehicule } from './fleetRules';
import { familleDuCamion, couleurFamille, libelleFamille } from './status';
import { nomLisible } from '../../lib/libelles';

/**
 * Commandes de la carte de flotte : zoom, tout recentrer, plein écran, « me
 * localiser » et recherche d'un camion. Tout ce qui est vrai de toute carte de
 * flotte vit ici, une fois — l'écran de suivi ne les redessine pas.
 */

const PASTILLE =
  'pointer-events-auto rounded-xl border border-border bg-card/90 shadow-sm backdrop-blur';
const BOUTON =
  'flex h-8 w-8 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-9 sm:w-9';
const SEPARATEUR = <span className="h-px w-full bg-border" aria-hidden="true" />;

/* ------------------------------------------------------------------ */
/* Briques à l'intérieur de la carte (accès à l'instance Leaflet)        */
/* ------------------------------------------------------------------ */

/** Donne l'instance Leaflet aux commandes, qui vivent hors de la carte (superposées). */
export function MapRef({ onReady }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
    return () => onReady(null);
  }, [map, onReady]);
  return null;
}

/**
 * La molette ne zoome la carte que si elle a le focus (clic, Tab) ou avec
 * Ctrl + molette. Sinon elle faisait défiler… la carte au lieu de la page, et
 * sur une page où la carte occupe l'écran, l'utilisateur ne pouvait plus
 * descendre voir la liste.
 */
export function WheelGuard({ onBlocked }) {
  const map = useMap();
  const blockedRef = useRef(onBlocked);
  blockedRef.current = onBlocked;

  useEffect(() => {
    const conteneur = map.getContainer();
    const actif = () => conteneur.contains(document.activeElement) || conteneur === document.activeElement;
    const activer = () => map.scrollWheelZoom.enable();
    const desactiver = () => map.scrollWheelZoom.disable();
    const entrer = () => {
      if (actif()) activer();
    };
    let dernierPas = 0;
    const surMolette = (event) => {
      if (map.scrollWheelZoom.enabled()) return;
      if (event.ctrlKey || event.metaKey) {
        // Sans cela le navigateur zoomerait la page entière.
        event.preventDefault();
        const maintenant = Date.now();
        if (maintenant - dernierPas < 120) return;
        dernierPas = maintenant;
        map.setZoomAround(map.mouseEventToContainerPoint(event), map.getZoom() + (event.deltaY < 0 ? 1 : -1));
      } else if (blockedRef.current) {
        blockedRef.current();
      }
    };

    desactiver();
    conteneur.addEventListener('mousedown', activer);
    conteneur.addEventListener('focusin', activer);
    conteneur.addEventListener('focusout', desactiver);
    conteneur.addEventListener('mouseleave', desactiver);
    conteneur.addEventListener('mouseenter', entrer);
    conteneur.addEventListener('wheel', surMolette, { passive: false });
    return () => {
      conteneur.removeEventListener('mousedown', activer);
      conteneur.removeEventListener('focusin', activer);
      conteneur.removeEventListener('focusout', desactiver);
      conteneur.removeEventListener('mouseleave', desactiver);
      conteneur.removeEventListener('mouseenter', entrer);
      conteneur.removeEventListener('wheel', surMolette);
    };
  }, [map]);

  return null;
}

/* ------------------------------------------------------------------ */
/* Écran tactile : deux doigts pour déplacer la carte                   */
/* ------------------------------------------------------------------ */

/** Vrai sur un écran tactile sans souris (téléphone, tablette). */
export const estEcranTactile = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

/**
 * Sur téléphone, la carte tient presque tout l'écran : glisser un doigt dessus
 * la déplaçait, et la page ne défilait plus jusqu'à la liste. Hors plein écran,
 * un doigt fait défiler la page et deux doigts déplacent et zooment la carte
 * (le pincement de Leaflet suit le centre des deux doigts). En plein écran, il
 * n'y a pas de page à faire défiler : un doigt suffit à nouveau.
 */
export function useGesteDeuxDoigts(map, actif, onBloque) {
  const bloqueRef = useRef(onBloque);
  bloqueRef.current = onBloque;

  useEffect(() => {
    if (!map || !map.dragging || !estEcranTactile()) return undefined;
    if (!actif) {
      map.dragging.enable();
      return undefined;
    }
    map.dragging.disable();
    const conteneur = map.getContainer();
    const surToucher = (event) => {
      if (event.touches && event.touches.length === 1 && bloqueRef.current) bloqueRef.current();
    };
    conteneur.addEventListener('touchstart', surToucher, { passive: true });
    return () => {
      conteneur.removeEventListener('touchstart', surToucher);
      map.dragging.enable();
    };
  }, [map, actif]);
}

/* ------------------------------------------------------------------ */
/* Plein écran                                                          */
/* ------------------------------------------------------------------ */

const CLASSE_PLEIN_ECRAN = 'fleet-map-plein-ecran';

/**
 * Plein écran : l'API Fullscreen quand le navigateur l'offre, sinon (iPhone) un
 * repli CSS qui fixe la carte sur toute la fenêtre. Échap sort dans les deux cas.
 */
export function usePleinEcran(obtenirCible) {
  const [actif, setActif] = useState(false);
  const modeRef = useRef(null); // 'natif' | 'css' | null
  const cibleRef = useRef(null);

  const quitterCss = useCallback(() => {
    if (cibleRef.current) cibleRef.current.classList.remove(CLASSE_PLEIN_ECRAN);
    document.body.style.removeProperty('overflow');
    modeRef.current = null;
    setActif(false);
  }, []);

  const entrerCss = useCallback((cible) => {
    cible.classList.add(CLASSE_PLEIN_ECRAN);
    document.body.style.overflow = 'hidden';
    modeRef.current = 'css';
    setActif(true);
  }, []);

  const basculer = useCallback(async () => {
    if (modeRef.current === 'css') {
      quitterCss();
      return;
    }
    if (modeRef.current === 'natif') {
      try {
        await document.exitFullscreen();
      } catch {
        setActif(false);
        modeRef.current = null;
      }
      return;
    }
    const cible = obtenirCible();
    if (!cible) return;
    cibleRef.current = cible;
    if (typeof cible.requestFullscreen === 'function') {
      try {
        await cible.requestFullscreen();
        modeRef.current = 'natif';
        setActif(true);
        return;
      } catch {
        // Refusé par le navigateur : on retombe sur le repli CSS.
      }
    }
    entrerCss(cible);
  }, [obtenirCible, entrerCss, quitterCss]);

  useEffect(() => {
    const surChangement = () => {
      if (modeRef.current === 'natif' && !document.fullscreenElement) {
        modeRef.current = null;
        setActif(false);
      }
    };
    const surTouche = (event) => {
      if (event.key === 'Escape' && modeRef.current === 'css') quitterCss();
    };
    document.addEventListener('fullscreenchange', surChangement);
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('fullscreenchange', surChangement);
      document.removeEventListener('keydown', surTouche);
      if (modeRef.current === 'css') quitterCss();
    };
  }, [quitterCss]);

  return { actif, basculer };
}

/* ------------------------------------------------------------------ */
/* Recherche d'un camion sur la carte                                   */
/* ------------------------------------------------------------------ */

const MAX_RESULTATS = 8;

const normaliser = (texte) =>
  String(texte || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** Camions dont la plaque ou le chauffeur contient le texte tapé (sans accent, sans casse). */
export const chercherCamions = (vehicules, terme) => {
  const t = normaliser(terme).trim();
  if (!t) return [];
  return (vehicules || [])
    .filter((v) => aUnePosition(v) && cleVehicule(v))
    .filter((v) =>
      [v.truck?.matricule, v.driver?.full_name, v.driver?.name].some((champ) => normaliser(champ).includes(t)),
    )
    .slice(0, MAX_RESULTATS);
};

function RechercheCamion({ vehicules, onChoisir, onFermer }) {
  const [terme, setTerme] = useState('');
  const champ = useRef(null);
  const resultats = useMemo(() => chercherCamions(vehicules, terme), [vehicules, terme]);

  useEffect(() => {
    champ.current?.focus();
  }, []);

  const choisir = (vehicule) => {
    onChoisir(cleVehicule(vehicule));
    onFermer();
  };

  return (
    <div className={`${PASTILLE} w-[min(18rem,calc(100vw-6rem))] p-2`}>
      <div className="flex items-center gap-1">
        <input
          ref={champ}
          type="search"
          value={terme}
          onChange={(e) => setTerme(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onFermer();
            if (e.key === 'Enter' && resultats[0]) choisir(resultats[0]);
          }}
          placeholder="Plaque ou chauffeur"
          aria-label="Chercher un camion sur la carte"
          className="input-base h-8 min-w-0 flex-1 text-xs"
        />
        <button type="button" onClick={onFermer} aria-label="Fermer la recherche" className={`${BOUTON} rounded-lg`}>
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {terme.trim() !== '' && (
        <ul className="mt-2 max-h-56 space-y-0.5 overflow-y-auto" aria-label="Camions trouvés">
          {resultats.length === 0 ? (
            <li className="px-2 py-1.5 text-xs text-muted-foreground">Aucun camion trouvé sur la carte</li>
          ) : (
            resultats.map((v) => (
              <li key={cleVehicule(v)}>
                <button
                  type="button"
                  onClick={() => choisir(v)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: couleurFamille(familleDuCamion(v)) }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-foreground">
                      {nomLisible(v.truck?.matricule, 'Camion sans plaque')}
                    </span>
                    <span className="block truncate text-muted-foreground">
                      {nomLisible(v.driver?.full_name, v.driver?.name, libelleFamille(familleDuCamion(v)))}
                    </span>
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pastille de commandes                                                */
/* ------------------------------------------------------------------ */

/**
 * @param {object} props
 * @param {L.Map|null} props.map  instance Leaflet (voir `MapRef`)
 * @param {Array} props.vehicles  camions visibles (recherche et « tout recentrer »)
 * @param {(cle: string|null) => void} props.onSelect  sélection d'un camion
 * @param {() => void} props.onRecenter  appelé après « tout recentrer »
 */
export default function FleetMapControls({ map, vehicles, onSelect, onRecenter, onGesteBloque }) {
  const [rechercheOuverte, setRechercheOuverte] = useState(false);
  const obtenirCible = useCallback(() => map?.getContainer()?.parentElement || null, [map]);
  const { actif: pleinEcran, basculer: basculerPleinEcran } = usePleinEcran(obtenirCible);
  useGesteDeuxDoigts(map, !pleinEcran, onGesteBloque);

  const toutRecentrer = useCallback(() => {
    if (!map) return;
    const points = (vehicles || []).filter(aUnePosition).map((v) => [Number(v.latitude), Number(v.longitude)]);
    // Retirer la sélection : sinon la carte, recentrée sur un seul camion, y reste.
    if (onSelect) onSelect(null);
    map.closePopup();
    if (points.length === 0) return;
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 14 });
    if (onRecenter) onRecenter();
  }, [map, vehicles, onSelect, onRecenter]);

  const seLocaliser = useCallback(() => {
    if (!map) return;
    map.once('locationerror', () =>
      toast.error('Votre position est introuvable. Autorisez la localisation dans le navigateur.'),
    );
    map.locate({ setView: true, maxZoom: 14 });
  }, [map]);

  return (
    <div className="pointer-events-none absolute left-3 top-3 z-[450] flex max-w-[calc(100%-1.5rem)] items-start gap-2">
      <div className={`flex flex-col overflow-hidden ${PASTILLE}`}>
        <button type="button" aria-label="Zoomer" onClick={() => map?.zoomIn()} className={BOUTON}>
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
        {SEPARATEUR}
        <button type="button" aria-label="Dézoomer" onClick={() => map?.zoomOut()} className={BOUTON}>
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        {SEPARATEUR}
        <button
          type="button"
          aria-label="Tout recentrer"
          title="Tout recentrer"
          onClick={toutRecentrer}
          className={BOUTON}
        >
          <Maximize className="h-4 w-4" aria-hidden="true" />
        </button>
        {SEPARATEUR}
        <button
          type="button"
          aria-label="Me localiser"
          title="Me localiser"
          onClick={seLocaliser}
          className={BOUTON}
        >
          <Crosshair className="h-4 w-4" aria-hidden="true" />
        </button>
        {SEPARATEUR}
        <button
          type="button"
          aria-label="Chercher un camion"
          title="Chercher un camion"
          aria-expanded={rechercheOuverte}
          onClick={() => setRechercheOuverte((v) => !v)}
          className={BOUTON}
        >
          <Search className="h-4 w-4" aria-hidden="true" />
        </button>
        {SEPARATEUR}
        <button
          type="button"
          aria-label={pleinEcran ? 'Quitter le plein écran' : 'Plein écran'}
          title={pleinEcran ? 'Quitter le plein écran' : 'Plein écran'}
          aria-pressed={pleinEcran}
          onClick={basculerPleinEcran}
          className={BOUTON}
        >
          {pleinEcran ? (
            <Minimize2 className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Expand className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
      {rechercheOuverte && (
        <RechercheCamion
          vehicules={vehicles}
          onChoisir={onSelect}
          onFermer={() => setRechercheOuverte(false)}
        />
      )}
    </div>
  );
}
